import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

/**
 * Cloudflare Turnstile on the sign-in screen (founder question 7). The site key is a public, build-time value
 * (VITE_TURNSTILE_SITE_KEY); without it nothing renders and no token is sent (local dev, tests, staging's test login).
 * The script is the only third-party script the app loads; the CSP allows exactly challenges.cloudflare.com (csp.ts).
 */
export const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  reset(id: string): void;
  remove(id: string): void;
}
declare global {
  interface Window { turnstile?: TurnstileApi }
}

export function turnstileSiteKey(): string {
  return ((import.meta.env?.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? "").trim();
}

let loading: Promise<TurnstileApi> | null = null;
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = TURNSTILE_SCRIPT;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile_missing")));
    s.onerror = () => { loading = null; reject(new Error("turnstile_load_failed")); };
    document.head.appendChild(s);
  });
  return loading;
}

export interface TurnstileHandle {
  /** Ask for a fresh token: each one is single-use, so call this after every send. */
  reset(): void;
}

export const Turnstile = forwardRef<TurnstileHandle, { siteKey: string; onToken: (token: string | null) => void; onError?: (code: string) => void }>(
  function Turnstile({ siteKey, onToken, onError }, ref) {
    const box = useRef<HTMLDivElement>(null);
    const widget = useRef<string | null>(null);
    const cb = useRef(onToken);
    cb.current = onToken;
    const errCb = useRef(onError);
    errCb.current = onError;

    useImperativeHandle(ref, () => ({
      reset() {
        cb.current(null);
        if (widget.current && window.turnstile) window.turnstile.reset(widget.current);
      },
    }), []);

    useEffect(() => {
      let gone = false;
      loadTurnstile()
        .then((api) => {
          if (gone || !box.current) return;
          widget.current = api.render(box.current, {
            sitekey: siteKey,
            action: "login",
            // Visible on purpose: a silent failure left people stuck with no clue (2026-11-05).
            appearance: "always",
            callback: (t: string) => { errCb.current?.(""); cb.current(t); },
            "expired-callback": () => cb.current(null),
            "error-callback": (code?: string | number) => {
              cb.current(null);
              const c = String(code ?? "unknown");
              console.warn("turnstile error", c);
              errCb.current?.(c);
              return true;
            },
          });
        })
        .catch((e: unknown) => { cb.current(null); errCb.current?.(e instanceof Error ? e.message : "load_failed"); });
      return () => {
        gone = true;
        if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
        widget.current = null;
      };
    }, [siteKey]);

    return <div ref={box} data-testid="turnstile" />;
  },
);
